import {
    createContext,
    useEffect,
    useState
} from "react";
import type {
    ReactNode
} from "react";
import AsyncStorage from "@react-native-async-storage/async-storage";

export interface User {
    organizations?: { id: string; name: string; profilePicturePath?: string; features?: string[] }[];
    role: "root" | "superAdmin" | "admin" | "user" | "guest";
    profilePicturePath?: string;
    org_id?: string | null;
    name_surname?: string;
    org_ids?: string[];
    fullName?: string;
    username: string;
    address?: string;
    email?: string;
    phone?: string;
    id?: string;
}

interface AuthContextType {
    changeActiveOrganization: (orgId: string | null, features?: string[]) => Promise<void>;
    login: (token: string, user: User) => Promise<void>;
    updateUser: (data: Partial<User>) => Promise<void>;
    activeOrganizationFeatures: string[];
    activeOrganizationId: string | null;
    guestLogin: () => Promise<void>;
    logout: () => Promise<void>;
    isAuthenticated: boolean;
    user: User | null;
    loading: boolean;
}

export const AuthContext = createContext<AuthContextType>({
    changeActiveOrganization: async () => {},
    activeOrganizationFeatures: [],
    activeOrganizationId: null,
    guestLogin: async () => {},
    updateUser: async () => {},
    isAuthenticated: false,
    logout: async () => {},
    login: async () => {},
    loading: true,
    user: null
});

export const AuthProvider = ({
    children
}: { children: ReactNode }) => {
    const [
        activeOrganizationId,
        setActiveOrganizationId
    ] = useState<string | null>(null);
    const [
        activeOrganizationFeatures,
        setActiveOrganizationFeatures
    ] = useState<string[]>([]);
    const [
        isAuthenticated,
        setIsAuthenticated
    ] = useState(false);
    const [
        user,
        setUser
    ] = useState<User | null>(null);
    const [
        loading,
        setLoading
    ] = useState(true);

    useEffect(() => {
        const loadAuth = async () => {
            try {
                const token = await AsyncStorage.getItem("authToken");
                const userData = await AsyncStorage.getItem("authUser");

                if (token && userData) {
                    let parsedUser = JSON.parse(userData) as User;
                    setUser(parsedUser);
                    setIsAuthenticated(true);

                    try {
                        const response = await fetch(`${process.env.API_URL}/auth/me`, {
                            headers: {
                                Authorization: `Bearer ${token}`
                            }
                        });

                        if (response.status === 401) {
                            await AsyncStorage.removeItem("activeOrganizationId");
                            await AsyncStorage.removeItem("authToken");
                            await AsyncStorage.removeItem("authUser");
                            await AsyncStorage.removeItem("authGuest");
                            await AsyncStorage.removeItem("activeOrganizationFeatures");

                            setUser(null);
                            setIsAuthenticated(false);
                            setLoading(false);

                            return;
                        }

                        const result = await response.json();
                        if (result.status === "success" && result.user) {
                            parsedUser = result.user as User;
                            setUser(parsedUser);
                            await AsyncStorage.setItem("authUser", JSON.stringify(parsedUser));
                        }
                    } catch (e) {
                        console.error("User fetch err:", e);
                    }

                    if (parsedUser.role === "root") {
                        const savedOrgId = await AsyncStorage.getItem("activeOrganizationId");
                        const savedFeatures = await AsyncStorage.getItem("activeOrganizationFeatures");
                        if (savedOrgId) {
                            setActiveOrganizationId(savedOrgId);
                        }
                        if (savedFeatures) {
                            try {
                                setActiveOrganizationFeatures(JSON.parse(savedFeatures));
                            } catch(e) {
                                console.error("err:", e);
                            }
                        }
                    } else if (parsedUser.organizations && parsedUser.organizations.length > 0) {
                        const savedOrgId = await AsyncStorage.getItem("activeOrganizationId");
                        let selectedOrg = parsedUser.organizations[0];
                        if (savedOrgId) {
                            const found = parsedUser.organizations.find(o => o.id === savedOrgId);
                            if (found) {
                                selectedOrg = found;
                            }
                        }
                        setActiveOrganizationId(selectedOrg?.id || null);

                        if (selectedOrg?.id) {
                            try {
                                const response = await fetch(`${process.env.API_URL}/organizations/${selectedOrg.id}`, {
                                    headers: {
                                        Authorization: `Bearer ${token}`
                                    }
                                });
                                const result = await response.json();
                                if (result.status === "success" && result.data) {
                                    setActiveOrganizationFeatures(result.data.features || []);
                                    selectedOrg.features = result.data.features || [];
                                    await AsyncStorage.setItem("authUser", JSON.stringify(parsedUser));
                                    await AsyncStorage.setItem("activeOrganizationFeatures", JSON.stringify(result.data.features || []));
                                } else {
                                    setActiveOrganizationFeatures(selectedOrg?.features || []);
                                }
                            } catch {
                                setActiveOrganizationFeatures(selectedOrg?.features || []);
                            }
                        } else {
                            setActiveOrganizationFeatures(selectedOrg?.features || []);
                        }
                    }
                } else {
                    const isGuest = await AsyncStorage.getItem("authGuest");
                    if (isGuest === "true") {
                        setUser({
                            username: "Misafir",
                            role: "guest"
                        });
                        setIsAuthenticated(true);
                    }
                }
            } catch (error) {
                console.error("Auth yüklenirken hata:", error);
            } finally {
                setLoading(false);
            }
        };

        loadAuth();
    }, []);

    const updateUser = async (data: Partial<User>) => {
        if (!user) return;
        const updatedUser = {
            ...user,
            ...data
        };
        setUser(updatedUser);
        await AsyncStorage.setItem("authUser", JSON.stringify(updatedUser));
    };

    const login = async (token: string, userData: User) => {
        try {
            setUser(userData);
            setIsAuthenticated(true);

            await AsyncStorage.setItem("authToken", token);
            await AsyncStorage.setItem("authUser", JSON.stringify(userData));

            if (userData.organizations && userData.organizations.length > 0) {
                const org = userData.organizations[0];
                setActiveOrganizationId(org?.id || null);
                setActiveOrganizationFeatures(org?.features || []);
                if (org?.id) {
                    await AsyncStorage.setItem("activeOrganizationId", org.id);
                    await AsyncStorage.setItem("activeOrganizationFeatures", JSON.stringify(org.features || []));
                }
            } else {
                setActiveOrganizationId(null);
                await AsyncStorage.removeItem("activeOrganizationId");
            }
        } catch (error) {
            console.error("Login işlemi kaydedilirken hata:", error);
        }
    };

    const guestLogin = async () => {
        try {
            const guestData: User = {
                username: "Misafir",
                role: "guest"
            };
            await AsyncStorage.setItem("authGuest", "true");
            setUser(guestData);
            setIsAuthenticated(true);
        } catch (error) {
            console.error("Misafir girişi kaydedilirken hata:", error);
        }
    };

    const changeActiveOrganization = async (orgId: string | null, features?: string[]) => {
        try {
            if (user?.role !== "root" && orgId) {
                const token = await AsyncStorage.getItem("authToken");
                if (token) {
                    const response = await fetch(`${process.env.API_URL}/users/switch-organization`, {
                        headers: {
                            "Content-Type": "application/json",
                            "Authorization": `Bearer ${token}`
                        },
                        body: JSON.stringify({
                            organization_id: orgId
                        }),
                        method: "PUT"
                    });

                    if (!response.ok) {
                        console.error("Organizasyon değiştirme başarısız.");
                        return;
                    }

                    const result = await response.json();
                    if (result.status === "success" && result.token) {
                        await AsyncStorage.setItem("authToken", result.token);
                    }
                }
            }

            setActiveOrganizationId(orgId);
            if (features) {
                setActiveOrganizationFeatures(features);
            } else if (orgId && user?.organizations) {
                const found = user.organizations.find(o => o.id === orgId);
                setActiveOrganizationFeatures(found?.features || []);
            } else {
                setActiveOrganizationFeatures([]);
            }

            if (orgId) {
                await AsyncStorage.setItem("activeOrganizationId", orgId);
                if (features) {
                    await AsyncStorage.setItem("activeOrganizationFeatures", JSON.stringify(features));
                } else if (user?.organizations) {
                    const found = user.organizations.find(o => o.id === orgId);
                    await AsyncStorage.setItem("activeOrganizationFeatures", JSON.stringify(found?.features || []));
                }
            } else {
                await AsyncStorage.removeItem("activeOrganizationId");
                await AsyncStorage.removeItem("activeOrganizationFeatures");
            }
        } catch (error) {
            console.error("Aktif organizasyon kaydedilirken hata:", error);
        }
    };

    const logout = async () => {
        try {
            await AsyncStorage.removeItem("activeOrganizationId");
            await AsyncStorage.removeItem("authToken");
            await AsyncStorage.removeItem("authUser");
            await AsyncStorage.removeItem("authGuest");
            setUser(null);
            setIsAuthenticated(false);
        } catch (error) {
            console.error("Logout sırasında hata:", error);
        }
    };

    return <AuthContext.Provider
        value={{
            activeOrganizationFeatures,
            changeActiveOrganization,
            activeOrganizationId,
            isAuthenticated,
            guestLogin,
            updateUser,
            loading,
            logout,
            login,
            user
        }}
    >
        {children}
    </AuthContext.Provider>;
};
